import { FC, useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { AuthContext } from "context/appContext";
import { getLocationByUserId, resetValues } from "redux/features/location/locationSlice";
import { Location } from "data/types";
import { itemPerDashboradClient } from "data/constants";
import ModernPendingSpacesHeader from "./ModernPendingSpacesHeader";
import ModernSpacesGrid from "./ModernSpacesGrid";

export interface ModernSpacesContainerProps {
  className?: string;
}

const ModernSpacesContainer: FC<ModernSpacesContainerProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const profilId = authContext?.user?.profil?.id;
  const [loading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const dispatch = useAppDispatch();
  const locationStates = useAppSelector((state) => state.location?.locationData || []) as Location[];

  // Filter non-approved locations (same logic as original component)
  const filteredLocations = Array.isArray(locationStates)
    ? locationStates.filter((location) => location?.validation !== "approuvé")
    : [];

  // Pagination
  const itemsPerPage = itemPerDashboradClient;
  const paginatedLocations = filteredLocations?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filteredLocations?.length / itemsPerPage);
  const totalCount = filteredLocations?.length || 0;

  // Statistics for header
  const pendingCount = filteredLocations.filter((loc) => loc.validation === "en attente").length;
  const draftCount = filteredLocations.filter((loc) => loc.validation === "brouillon").length;
  const blockedCount = filteredLocations.filter((loc) => loc.validation === "bloqué").length;
  const processingCount = filteredLocations.filter((loc) => loc.validation === "en cours").length;

  // Load locations
  useEffect(() => {
    if (profilId) {
      setIsLoading(true);
      dispatch(getLocationByUserId(String(profilId))).finally(() => setIsLoading(false));
    }
  }, [profilId, dispatch]);

  // Reset values on mount
  useEffect(() => {
    dispatch(resetValues());
  }, [dispatch]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleAddSpace = () => {
    navigate("/add-location");
  };

  const handleFilter = () => {
    // TODO: Implement filter functionality
    console.log("Filter clicked");
  };

  const handleDeleteSpace = (locationId: string) => {
    // TODO: Implement delete functionality
    console.log("Delete space:", locationId);
  };

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-gray-50/50 via-emerald-50/20 to-blue-50/30 dark:from-gray-900 dark:via-emerald-950/20 dark:to-blue-950/30 ${className}`}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="container mx-auto px-4 py-8"
      >
        {/* Header */}
        <ModernPendingSpacesHeader
          totalCount={totalCount}
          pendingCount={pendingCount}
          draftCount={draftCount}
          blockedCount={blockedCount}
          processingCount={processingCount}
          onAddSpace={handleAddSpace}
          onFilter={handleFilter}
        />

        {/* Spaces Grid */}
        <ModernSpacesGrid
          loading={loading}
          locations={paginatedLocations}
          totalCount={totalCount}
          totalPages={totalPages}
          currentPage={currentPage}
          isCustomer={authContext?.user?.isCustomer}
          onPageChange={handlePageChange}
          onAddSpace={handleAddSpace}
          onDeleteSpace={handleDeleteSpace}
        />

        {/* Background Decorations */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-emerald-400/20 dark:bg-emerald-500/10 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 0.7, 0.3],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 8 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 8,
              }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default ModernSpacesContainer;
